import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      // If returning to a specific page, honour that. Otherwise route to the
      // dashboard for the program they actually own.
      let next = from;
      if (from === "/dashboard") {
        const u = res.user || {};
        if (u.has_mens_guide_access && !u.has_making_fabulous_access) next = "/dashboard/mens-guide";
        else if (u.has_menopause_invitation_access && !u.has_making_fabulous_access && !u.has_mens_guide_access) next = "/dashboard/menopause";
      }
      navigate(next, { replace: true });
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-login">
      <div className="hidden lg:block lg:w-1/2 bg-sage relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-cream max-w-md">
            <div className="font-serif-display text-5xl leading-[1.05]">
              Welcome back.
            </div>
            <p className="mt-6 text-cream/70 leading-relaxed">
              Your work continues. The meditations, the modules, the quiet shifts — they're all waiting.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="login-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
          <h1 className="font-serif-display text-4xl text-ink mt-10 leading-tight">Sign in</h1>
          <p className="mt-3 text-ink-soft">Enter your account to continue.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="eyebrow block mb-2 text-ink-soft">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2 text-ink-soft">Password</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
            </button>
          </form>

          <p className="mt-8 text-sm text-ink-soft">
            New here?{" "}
            <Link to="/register" className="text-sage hover:text-sage-dark font-medium" data-testid="login-to-register">
              Create an account
            </Link>
            <span className="mx-2 text-ink-soft/40">·</span>
            <Link to="/forgot-password" className="text-sage hover:text-sage-dark font-medium" data-testid="login-forgot-password">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
